import moment from "moment";
import instance from "../../axios";
import { getCompanies, prepareUserForLogin, checkUserRoles, prepareUsers } from "../../Helpers";
import { asArray, parseApiError, toSelectOption, toQuery, unwrapPage } from "../identity/apiError";
import { contractTypeFromTab } from "../identity/formDefaults";
import { buildCreatePayload, replaceHtmlEntities } from "../identity/formatters";

export const listUsers = async ({
  userId,
  page = 1,
  pageSize = 20,
  search = "",
  tab = "maintenance",
  extraQuery = "",
  accountId,
}) => {
  const contractType = contractTypeFromTab(tab);
  const query = toQuery({
    userId,
    all: true,
    page,
    limit: pageSize,
    contractType,
    search,
    ...(accountId ? { accountId } : {}),
  });
  const payload = await instance.get(`/api/users/get?${query}${extraQuery || ""}`);
  return unwrapPage(payload);
};

export const mapUsersForTable = (rows, roles, logInUser) => {
  const prepared = prepareUserForLogin(checkUserRoles(prepareUsers(rows), roles));
  return (prepared || []).map((item) => {
    const user = replaceHtmlEntities(item);
    if (!user.created) return user;
    return {
      ...user,
      createdOriginal: user.created,
      created: logInUser?.twelveHourFormat
        ? moment(user.created).format("YYYY-MM-DD hh:mm A")
        : moment(user.created).format("YYYY-MM-DD HH:mm"),
    };
  });
};

export const createIdentity = async (form, accountsform, extras = {}) => {
  const { url, body } = buildCreatePayload(form, accountsform, extras);
  return instance.post(url, body);
};

export const updateUser = (id, body) => instance.put(`/api/users/${id}`, body);
export const deleteUser = (user) =>
  instance.delete(`/api/users/${user.id}`, { data: user });
export const activateUser = (id, active) =>
  instance.put(`/api/users/${active ? "active" : "inactive"}/${id}`);
export const enableUser = (id, enableAllChild) =>
  instance.get(`/api/users/enable?userId=${id}&enableAllChild=${!!enableAllChild}`);
export const disableUser = (id, disableAllChild) =>
  instance.get(`/api/users/disable?userId=${id}&disableAllChild=${!!disableAllChild}`);
export const migrateUser = (newParentId, userId) =>
  instance.post("/api/users/migrate", { newParentId, userId: [userId] });

export const assignPermission = (payload) => instance.post("/api/permissions", payload);

export const countDevices = (userId) => instance.get(`/api/devices/count?userId=${userId}`);
export const countUsers = (userId) => instance.get(`/api/users/count?userId=${userId}`);
export const countVehicles = (userId) => instance.get(`/api/vehicles/count?userId=${userId}`);

export const listAccountManagers = async (search = "") => {
  const data = await instance.get(
    `/api/users/getAllUsersInSystem?search=${encodeURIComponent(search)}&userTypes=4`,
  );
  return asArray(data).map(toSelectOption);
};

export const listVehicles = async (userId) => {
  const data = await instance.get(`/api/vehicles/list?userId=${userId}&page=1&limit=100`);
  const rows = data?.data || data || [];
  return asArray(rows).map(toSelectOption);
};

export const listCompanies = async () => {
  const data = await getCompanies();
  return asArray(data).map(toSelectOption);
};

export const listGarages = async (userId, search = "") => {
  const data = await instance.get(
    `/api/garages/get?userId=${userId}&page=1&limit=50&all=true${
      search ? `&search=${encodeURIComponent(search)}` : ""
    }`,
  );
  return asArray(data).map((item) => ({
    id: item.id,
    key: item.id,
    name: `${item.name} - ${item.garageNumber}`,
    label: `${item.name} - ${item.garageNumber}`,
    uniqueId: item.id,
    valueType: "string",
    garageNumber: item.garageNumber,
  }));
};

export const listParentUsers = async (accountId, logInUser, search = "") => {
  const all = logInUser?.userType === -1 ? "&all=true" : "";
  const data = await instance.get(
    `/api/users/list?accountId=${accountId}&limit=100&search=${encodeURIComponent(search)}${all}`,
  );
  return asArray(data).map(toSelectOption);
};

export const listAccountsForPicker = async (accountId, logInUser, page = 1, search = "") => {
  const all = logInUser?.userType === -1 ? "&all=true" : "";
  const data = await instance.get(
    `/api/accounts/all?accountId=${accountId}${all}&page=${page}&limit=100&search=${encodeURIComponent(
      search,
    )}`,
  );
  return asArray(data).map(toSelectOption);
};

export const listAccountAdmins = async (userId, roles) => {
  const data = await instance.get(
    `/api/users/get?userId=${userId}&all=true&page=1&limit=200&search=&userTypes=3`,
  );
  const rows = asArray(data);
  return prepareUserForLogin(checkUserRoles(prepareUsers(rows), roles)) || [];
};

export const getAccountById = async (accountId) => {
  const data = await instance.get(`/api/accounts?accountId=${accountId}`);
  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.data)) return data.data[0] || null;
  return data || null;
};

export const getAccountTerms = (accountId) =>
  instance.get(`/api/accounts/${accountId}/terms`);

export const getUserFiles = (userId) =>
  instance.get(`/api/uploads/get?entityId=${userId}&entity=User`);

export const deleteUpload = (mediaId) => instance.delete(`/api/uploads/${mediaId}`);

export const getLinkedDevices = (userId) =>
  instance.get(`/api/devices/get?userId=${userId}&all=true&limit=-1`);

export const getLinkedDeviceIds = (userId) =>
  instance.get(`/api/devices/linked?userId=${userId}`);

export const listAllUserIds = async (userId) => {
  const payload = await instance.get(
    `/api/users/get?userId=${userId}&all=true&limit=-1`,
  );
  return unwrapPage(payload).data || [];
};

export const handleIdentityError = (error, translate, fallback) =>
  parseApiError(error, translate) || fallback;
